import { useState } from 'react';
import { ScrollReveal } from '@/components/Animations';
import { Phone, Mail, MapPin, Clock, Send, Navigation, ExternalLink } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPS_ADDRESS = '1335+Boulevard+Bona-Dussault,+Saint-Marc-des-Carrières,+QC+G0A+4B0';
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_ADDRESS}`;
const MAPS_URL = `https://www.google.com/maps/place/${MAPS_ADDRESS}`;
const EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2700!2d-72.0489!3d46.6847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cb8a1a1a1a1a1a1%3A0x0!2s1335+Boulevard+Bona-Dussault%2C+Saint-Marc-des-Carri%C3%A8res%2C+QC+G0A+4B0!5e0!3m2!1sfr!2sca!4v1700000000000!5m2!1sfr!2sca';

function MapWidget() {
  const { isDark } = useTheme();
  return (
    <div className="relative w-full overflow-hidden" style={{ height: '380px' }} data-testid="contact-map-widget">
      {/* Map iframe with dark filter */}
      <div className="absolute inset-0" style={{
        filter: isDark ? 'invert(90%) hue-rotate(180deg) brightness(0.8) contrast(1.1)' : 'none',
      }}>
        <iframe
          src={EMBED_URL}
          width="100%" height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Concept Gourmet - Localisation"
        />
      </div>

      {/* "Obtenir l'itinéraire" button - top right */}
      <a
        href={DIRECTIONS_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="map-directions-btn"
        className="absolute top-4 right-4 z-10 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
        style={{
          backgroundColor: isDark ? 'rgba(18,22,17,0.9)' : 'rgba(255,255,255,0.95)',
          color: isDark ? '#FAF9F6' : '#1A2318',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
        }}
      >
        <Navigation size={16} style={{ color: 'var(--color-primary)' }} />
        Obtenir l'itinéraire
      </a>

      {/* "Ouvrir dans Maps" bar - bottom */}
      <a
        href={MAPS_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="map-open-btn"
        className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-all duration-200 hover:opacity-90"
        style={{
          backgroundColor: isDark ? 'rgba(18,22,17,0.92)' : 'rgba(45,45,45,0.92)',
          color: isDark ? 'var(--color-primary)' : 'var(--color-primary)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <MapPin size={15} />
        Ouvrir dans Maps
      </a>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', city: '', message: '' });
    } catch {
      setStatus('error');
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 sm:py-28 px-4">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-4 font-semibold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--color-primary)' }}>
              Parlons ensemble
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Contactez-nous !
            </h1>
            <p className="text-lg" style={{ color: 'var(--color-muted-fg)' }}>
              Vous êtes intéressé par nos services et vous avez besoin de renseignements ? Prenez contact avec nous via le formulaire ci-dessous !
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Form + Info */}
      <section className="pb-20 sm:pb-32 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Form */}
          <div className="lg:col-span-7">
            <ScrollReveal>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Nom *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                    className="w-full px-4 py-3 rounded-xl text-base transition-all duration-300 outline-none"
                    style={{
                      backgroundColor: 'var(--color-muted)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-fg)',
                    }}
                    placeholder="Votre nom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Adresse courriel *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                    className="w-full px-4 py-3 rounded-xl text-base transition-all duration-300 outline-none"
                    style={{
                      backgroundColor: 'var(--color-muted)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-fg)',
                    }}
                    placeholder="votre@courriel.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      let fmt = digits;
                      if (digits.length > 3 && digits.length <= 6) fmt = `${digits.slice(0,3)} ${digits.slice(3)}`;
                      else if (digits.length > 6) fmt = `${digits.slice(0,3)} ${digits.slice(3,6)}-${digits.slice(6)}`;
                      setForm({ ...form, phone: fmt });
                    }}
                    placeholder="418 555-1234"
                    data-testid="contact-phone-input"
                    className="w-full px-4 py-3 rounded-xl text-base transition-all duration-300 outline-none"
                    style={{
                      backgroundColor: 'var(--color-muted)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-fg)',
                    }}
                    placeholder="Votre numéro de téléphone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Ville *</label>
                  <input
                    type="text"
                    required
                    value={form.city}
                    onChange={e => setForm({ ...form, city: e.target.value })}
                    data-testid="contact-city-input"
                    className="w-full px-4 py-3 rounded-xl text-base transition-all duration-300 outline-none"
                    style={{
                      backgroundColor: 'var(--color-muted)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-fg)',
                    }}
                    placeholder="Votre ville"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message-input"
                    className="w-full px-4 py-3 rounded-xl text-base transition-all duration-300 outline-none resize-none"
                    style={{
                      backgroundColor: 'var(--color-muted)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-fg)',
                    }}
                    placeholder="Votre message..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-medium transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  {loading ? 'Envoi...' : 'Envoyer le message'}
                  <Send size={16} />
                </button>

                {status === 'success' && (
                  <p data-testid="contact-success" className="text-base font-medium" style={{ color: 'var(--color-secondary)' }}>
                    Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                  </p>
                )}
                {status === 'error' && (
                  <p data-testid="contact-error" className="text-base font-medium" style={{ color: 'var(--color-accent)' }}>
                    Une erreur est survenue. Veuillez réessayer.
                  </p>
                )}
              </form>
            </ScrollReveal>
          </div>

          {/* Coordonnées */}
          <div className="lg:col-span-5">
            <ScrollReveal delay={0.2}>
              <div className="p-8 rounded-2xl space-y-6" style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                <h3 className="text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Coordonnées</h3>

                <div className="space-y-1">
                  <p className="text-lg font-semibold">CONCEPT GOURMET</p>
                  <p className="text-base">Service de Traiteur</p>
                  <p className="text-base font-medium">Caroline Bélanger</p>
                </div>

                <a href="tel:4182857251" className="flex items-center gap-3 text-lg font-medium hover:text-[var(--color-primary)] transition-colors" data-testid="contact-phone">
                  <Phone size={20} style={{ color: 'var(--color-primary)' }} />
                  418-285-7251
                </a>

                <div className="flex items-start gap-3">
                  <MapPin size={20} className="mt-1 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  <div className="text-base">
                    <p>Situé à :</p>
                    <p className="font-medium">1335, boulevard Bona-Dussault</p>
                    <p>St-Marc-des-Carrières, Qc</p>
                    <p>G0A 4B0</p>
                  </div>
                </div>

                <a href="mailto:info@conceptgourmet.ca" className="flex items-center gap-3 text-base hover:text-[var(--color-primary)] transition-colors" data-testid="contact-email">
                  <Mail size={20} style={{ color: 'var(--color-primary)' }} />
                  info@conceptgourmet.ca
                </a>

                <div className="flex items-start gap-3 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
                  <Clock size={20} className="mt-1 flex-shrink-0" style={{ color: 'var(--color-secondary)' }} />
                  <div className="text-base">
                    <p className="font-semibold mb-2">Heures d'ouverture été</p>
                    <p>Lundi au vendredi = 9h00 à 16h30</p>
                    <p>Samedi et dimanche = Fermé</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Map Section - MidiGo Style */}
      <section className="pb-0" data-testid="contact-map-section">
        <div className="max-w-6xl mx-auto px-4 mb-8">
          <ScrollReveal>
            <h3 className="text-2xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Nous trouver
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-muted-fg)' }}>
              1335, boulevard Bona-Dussault, St-Marc-des-Carrières, QC G0A 4B0
            </p>
          </ScrollReveal>
        </div>
        <MapWidget />
      </section>
    </div>
  );
}
